import React, { createContext, useContext, useEffect, useState } from "react";
import { getFileExt } from "../utils";
import { v4 as uuid_v4 } from "../libs/uuid";
import { apiUploadFileToS3 } from "../graphQL/file";

const UploadContext = createContext(null);
UploadContext.displayName = "UploadContext";

export function useUploadContext() {
  return useContext(UploadContext);
}

export function UploadProvider({ children }) {
  const [queue, setQueue] = useState([]);
  const [updated, setUpdated] = useState(null);

  const endQueueId = (endedQueueId) => {
    setQueue(queue.filter((item) => item.id !== endedQueueId));
  };

  const inferMimeType = (file) => {
    const explicit = String(file?.type || "").trim();
    if (explicit) return explicit;
    const ext = String(getFileExt(file?.name || "") || "").toLowerCase();
    if (ext === "png") return "image/png";
    if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
    if (ext === "webp") return "image/webp";
    return "application/octet-stream";
  };

  const uploadAvatar = (id, file, callback) => {
    const extFromName = String(getFileExt(file?.name || "") || "").toLowerCase();
    const extFromMime =
      inferMimeType(file) === "image/jpeg"
        ? "jpg"
        : inferMimeType(file) === "image/webp"
        ? "webp"
        : "png";
    const uploadFileName = `${uuid_v4()}.${extFromName || extFromMime}`;
    const key = `${id}/${uploadFileName}`;
    return apiUploadFileToS3(key, file, {
      contentType: inferMimeType(file),
    })
      .then(() => {
        callback(null, { key }, uploadFileName);
        return uploadFileName;
      })
      .catch((err) => {
        callback(err, null, null);
      });
  };

  const upload = (type, id, file, fileName, callback) => {
    const key = `${id}/${fileName}`;
    const promise = apiUploadFileToS3(key, file, {
      contentType: file.type,
      onProgress: (progress) => {
        const pro = Math.round((progress.loaded * 100) / progress.total);
        setUpdated({ id, progress: pro });
      },
    })
      .then(() => {
        endQueueId(id);
        callback(null, { key }, () => {
          setQueue(queue.filter((item) => item.id !== id));
        });
      })
      .catch((err) => {
        endQueueId(id);
        callback(err, null, () => {});
      });
    setQueue([...queue, { type, id, progress: 0, file, promise }]);
  };

  const uploadSingle = (id, file, fileName, onProgress, callback, options = {}) => {
    const key = options?.key || `${id}/${fileName}`;
    apiUploadFileToS3(key, file, {
      contentType: file.type,
      isPublic: Boolean(options?.isPublic),
      onProgress: (progress) => {
        const pro = Math.round((progress.loaded * 100) / progress.total);
        onProgress(pro);
      },
    })
      .then(() => {
        callback(null, { key }, () => {});
      })
      .catch((err) => {
        callback(err, null, () => {});
      });
  };

  const uploadJsonFile = (key, file, onProgress, callback) => {
    apiUploadFileToS3(key, file, {
      contentType: file.type,
      onProgress: (progress) => {
        const pro = Math.round((progress.loaded * 100) / progress.total);
        onProgress(pro);
      },
    })
      .then(() => {
        callback(null, { key }, () => {});
      })
      .catch((err) => {
        callback(err, null, () => {});
      });
  };

  useEffect(() => {
    if (updated && queue.find((item) => item.id === updated?.id)) {
      setQueue(
        queue.map((item) =>
          item.id === updated.id
            ? { ...item, progress: updated?.progress }
            : item
        )
      );
    }
  }, [updated]); //eslint-disable-line

  return (
    <UploadContext.Provider
      value={{
        queue,
        upload,
        uploadAvatar,
        uploadSingle,
        uploadJsonFile,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
}

export default UploadContext;
